import { useState, useMemo, useEffect } from "react"
import { useRef } from "react";
import { useUser } from '../redux/useUser';
import { useLocation } from 'react-router-dom';
import "../styles/profile.scss"
import Layout from "../components/Layout"
import profilePhoto from "../images/profile.png"
import coverPhoto from "../images/cover.png"
import 'bootstrap-icons/font/bootstrap-icons.css';
import Select from "react-select"
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from '../redux/updateUserSlice';
import { setUser } from '../redux/userSlice';
import countries from "world-countries"
import { InputMask } from "@react-input/mask"
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Formik, Form, Field, ErrorMessage } from "formik"
import * as Yup from "yup"
import Loader from "../components/Loader";
import { showSuccessToast, showErrorToast } from "../components/Toast";
import userpic from "../images/user.png"
import { uploadImage } from '../utils/uploadImage';
import { uploadToS3, calculateSHA256 } from '../utils/uploadToS3';
import { api, getAuthHeaders } from '../utils/api';
import axios from 'axios';
import { fetchMfaStatus, enableMfa, disableMfa } from '../redux/mfaSlice';
import { webAuth } from '../utils/auth0'; // Import webAuth from utils
import EmailPhoneLink from './EmailPhoneLink';
import CountrySelectReact from '../utils/CountrySelect';
import { getDialCodeFromUserProfile } from '../utils/localeDetection';
import useepic from '../images/default_picture.jpg';
import SubscriptionManagement from '../components/SubscriptionManagement';

import { Button } from "react-bootstrap";


// Create currency options (restricted)
const currencyOptions = [
  { value: "NZD", label: "NZD", symbol: "NZ$" },
  { value: "AUD", label: "AUD", symbol: "A$" },
  { value: "USD", label: "USD", symbol: "$" },
  { value: "INR", label: "INR", symbol: "₹" },
  { value: "EUR", label: "EUR", symbol: "€" },
];

const EMPTY_PROFILE_PLACEHOLDERS = new Set(['na', 'n/a', 'null', 'undefined']);

function isEmptyProfileValue(value) {
  if (value === null || value === undefined) return true;
  const str = String(value).trim();
  if (!str) return true;
  return EMPTY_PROFILE_PLACEHOLDERS.has(str.toLowerCase());
}

function sanitizeProfileField(value) {
  return isEmptyProfileValue(value) ? '' : String(value).trim();
}

function displayProfileValue(value, fallback = 'NA') {
  return isEmptyProfileValue(value) ? fallback : String(value).trim();
}

function formatDateOfBirthDisplay(dob) {
  if (isEmptyProfileValue(dob)) return 'NA';
  if (typeof dob === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dob)) return dob;

  let dateObj = dob;
  if (typeof dob === 'string') {
    const [day, month, year] = dob.split(/[\/\-]/);
    if (day && month && year) {
      dateObj = new Date(`${year}-${month}-${day}`);
    } else {
      dateObj = new Date(dob);
    }
  }

  if (dateObj instanceof Date && !isNaN(dateObj)) {
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}/${month}/${year}`;
  }

  return displayProfileValue(dob);
}

// Utility to safely parse date string to Date object or return null
function parseDateSafe(date) {
  if (!date) return null;
  if (date instanceof Date && !isNaN(date)) return date;
  // Try to parse string in dd/MM/yyyy or ISO format
  if (typeof date === 'string') {
    // Try ISO first
    const iso = new Date(date);
    if (!isNaN(iso)) return iso;
    // Try dd/MM/yyyy
    const parts = date.split('/');
    if (parts.length === 3) {
      const [dd, mm, yyyy] = parts;
      const parsed = new Date(`${yyyy}-${mm}-${dd}`);
      if (!isNaN(parsed)) return parsed;
    }
  }
  return null;
}

// Validation schemas
const basicDetailsSchema = Yup.object().shape({
  fullName: Yup.string()
    .min(3, "Name must be at least 3 characters")
    .max(50, "Name must be less than 50 characters")
    .required("Full name is required")
    .matches(
      /^[a-zA-Z\s\-'.]{3,50}$/,
      "Only letters, spaces, hyphens, apostrophes, and periods are allowed."
    ),
  dateOfBirth: Yup.mixed()
    .test('required', 'Date of birth is required', value => !!value)
    .test('valid', 'Date must be a valid date', value => {
      if (!value) return false;
      if (value instanceof Date && !isNaN(value)) return true;
      if (typeof value === 'string') {
        const d = parseDateSafe(value);
        return d instanceof Date && !isNaN(d);
      }
      return false;
    }),
  defaultCurrency: Yup.string().required("Currency is required"),
  gender: Yup.string().required("Gender is required"),
  email: Yup.string().email("Invalid email address").required("Email is required"),
  address: Yup.string()
    .min(3, "Address must be at least 3 characters")
    .max(150, "Address must be less than 150 characters")
    .required("Address is required")
    .matches(
      /^[a-zA-Z0-9\s,.\-#/'()]{3,150}$/,
      "Only letters, numbers, spaces, and common symbols (, . - / # ' ( )) are allowed."
    )
    .matches(
      /^\S(?:.*\S)?$/,
      "Space at the beginning or end is not allowed"
    ),
  // phoneCode: Yup.string().required("Phone code is required"),
  // phoneNumber: Yup.string()
  //   .matches(/^\d{4} \d{4} \d{2}$/, "Phone number must be in XXXX XXXX XX format")
  //   .required("Phone number is required"),
  country: Yup.string().required("Country is required"),
  bio: Yup.string()
    .min(3, "Bio must be at least 3 characters")
    .max(300, "Bio must be less than 300 characters")
    .matches(
      /^[a-zA-Z0-9\s,.\-#/'()]{3,300}$/,
      "Only letters, numbers, spaces, and common symbols (, . - / # ' ( )) are allowed."
    ),
})

const getSafeCalendarDate = (selectedDate) => {
  const today = new Date();

  if (!selectedDate) return today;

  // If selected date goes to future → clamp to today
  return selectedDate > today ? today : selectedDate;
};
const socialLinksSchema = Yup.object().shape({
  facebook: Yup.string()
    .trim()
    .test('is-url-or-empty', 'Invalid Facebook URL', value => {
      if (!value || value.trim() === '') return true;
      try {
        new URL(value.startsWith('http') ? value : 'https://' + value);
        return true;
      } catch {
        return false;
      }
    })
    .nullable()
    .notRequired(),
  instagram: Yup.string()
    .trim()
    .test('is-url-or-empty', 'Invalid Instagram URL', value => {
      if (!value || value.trim() === '') return true;
      try {
        new URL(value.startsWith('http') ? value : 'https://' + value);
        return true;
      } catch {
        return false;
      }
    })
    .nullable()
    .notRequired(),
  youtube: Yup.string()
    .trim()
    .test('is-url-or-empty', 'Invalid YouTube URL', value => {
      if (!value || value.trim() === '') return true;
      try {
        new URL(value.startsWith('http') ? value : 'https://' + value);
        return true;
      } catch {
        return false;
      }
    })
    .nullable()
    .notRequired(),
  twitter: Yup.string()
    .trim()
    .test('is-url-or-empty', 'Invalid Twitter URL', value => {
      if (!value || value.trim() === '') return true;
      try {
        new URL(value.startsWith('http') ? value : 'https://' + value);
        return true;
      } catch {
        return false;
      }
    })
    .nullable()
    .notRequired(),
});


const otpSchema = Yup.object().shape({
  otp1: Yup.string().matches(/^\d$/, "Must be a single digit").required("Required"),
  otp2: Yup.string().matches(/^\d$/, "Must be a single digit").required("Required"),
  otp3: Yup.string().matches(/^\d$/, "Must be a single digit").required("Required"),
  otp4: Yup.string().matches(/^\d$/, "Must be a single digit").required("Required"),
})

// Helper to map frontend userData to backend API schema
function mapUserDataToApiPayload(userData) {
  // Only allow 'male', 'female', 'other' (lowercase) for gender
  let gender = (userData.gender || '').toLowerCase();
  if (!['male', 'female', 'other'].includes(gender)) gender = '';
  // Always set region to the same value as currency
  const currency = userData.defaultCurrency;
  return {
    name: userData.fullName,
    dateOfBirth: userData.dateOfBirth,
    currency,
    region: currency,
    gender,
    email: userData.email,
    address: {
      street: userData?.address,
      country: userData?.country,
    },
    phone: userData.phoneCode
      ? `+${userData.phoneCode}${userData.phoneNumber.replace(/\s+/g, '')}`
      : `${userData.phoneNumber.replace(/\s+/g, '')}`,
    bio: userData.bio,
    // profileImage: userData.profileImage,
    // coverImage: userData.coverImage,
    // socialLinks: userData.socialLinks,
  };
}

const ProfileManagement = () => {
  const location = useLocation();
  
  // Check URL params for tab selection
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    return ['profile', 'billing', 'legacyAccessSetting'].includes(tab) ? tab : 'profile';
  };

  // Get MFA status data from Redux (must be inside component)
  const { status: mfaStatus, loading: mfaLoading, error: mfaError, enableLoading, enableError, enableSuccess } = useSelector((state) => state.mfa || {});
  const [showMfaPopup, setShowMfaPopup] = useState(false);
  const [mfaTicketUrl, setMfaTicketUrl] = useState("");
  const [socialUpdating, setSocialUpdating] = useState(false);
  const [detailsUpdating, setDetailsUpdating] = useState(false);
  const { user, loading, error, getUser } = useUser();
const [progresses, setProgresses] = useState({});
  const [successMessages, setSuccessMessages] = useState({});
  const [errorMessages, setErrorMessages] = useState({});
  const [mfaEnabled, setMfaEnabled] = useState({});
  const [unlinking, setUnlinking] = useState(false);
  const [code, setCode] = useState('+91');
const [phoneNumber, setPhoneNumber] = useState('');
  const [socialActiveTab, setSocialActiveTab] = useState('google');
const loginWithSocial = (provider) => {
    const state = JSON.stringify({ returnTo: '/dashboard' });
    webAuth.authorize({
      connection: provider,
      redirectUri: window.location.origin + '/callback',
      responseType: 'token id_token',
      scope: 'openid profile email phone',
      state,
        // prompt: 'none',
    });
  };
// Legacy Access Edit Modal State
const [showLegacyEditModal, setShowLegacyEditModal] = useState(false);
const [legacyEditData, setLegacyEditData] = useState({ inactivityDays: '', gracePeriodDays: '', notes: '' });
const [legacyEditLoading, setLegacyEditLoading] = useState(false);
const [legacyEditError, setLegacyEditError] = useState('');
const [legacyAccessData, setLegacyAccessData] = useState({ inactivityDays: '', gracePeriodDays: '', notes: '' });

// Handler to open modal and set data
const handleLegacyEditClick = async () => {
  try {
    const accessToken = localStorage.getItem('access_token');
    const res = await api.get('/sharing/legacy', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    const data = res.data;
    setLegacyAccessData(data);
    setLegacyEditData({
      inactivityDays: data?.triggerSettings?.inactivityDays || '',
      gracePeriodDays: data?.triggerSettings?.gracePeriodDays || '',
      notes: data?.notes || '',
    });
    setShowLegacyEditModal(true);
  } catch (err) {
    console.error('Failed to fetch legacy data:', err);
  }
};

// Handler to update legacy access settings
const handleLegacyEditSave = async () => {
  setLegacyEditLoading(true);
  setLegacyEditError('');
  const accessToken = localStorage.getItem('access_token');
  try {
    const payload = {
      inactivityDays: Number(legacyEditData.inactivityDays),
      gracePeriodDays: Number(legacyEditData.gracePeriodDays),
      notes: legacyEditData.notes,
    };
    const res = await api.put('/sharing/legacy', payload, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (res.status === 200) {
      setLegacyAccessData(prev => ({
        ...prev,
        triggerSettings: {
          ...prev?.triggerSettings,
          inactivityDays: Number(legacyEditData.inactivityDays),
          gracePeriodDays: Number(legacyEditData.gracePeriodDays),
        },
        notes: legacyEditData.notes,
      }));
      setShowLegacyEditModal(false);
      showSuccessToast('Legacy access updated successfully!');
    } else {
      setLegacyEditError('Failed to update legacy access.');
    }
  } catch (err) {
    setLegacyEditError('Failed to update legacy access.');
  } finally {
    setLegacyEditLoading(false);
  }
};
// helper to find an identity object by provider key (provider or connection)
const findIdentity = (providerKey) => {
  return Array.isArray(identities) ? identities.find(i => i.provider === providerKey || i.connection === providerKey) : null;
}
const handleUnlinkIdentity = async (identity, e) => {
  if (e && typeof e.stopPropagation === 'function') e.stopPropagation();

  if (!identity) return;

  if (unlinking) {
    return;
  }

  // safety: don't allow removing last login method
  const totalIdentities = Array.isArray(identities) ? identities.length : 0;
  if (totalIdentities <= 1) {
    showErrorToast('Cannot unlink the only login method. Add another method first.');
    return;
  }

  setUnlinking(true);

  try {
    const payload = {
      provider: identity.provider || null,
      userId: identity.userId || null,
      connection: identity.connection || null,
      isSocial: !!identity.isSocial,
      identifier: identity.identifier || null,
    };

     const accessId = localStorage.getItem("access_token");

    const res = await api.post('/users/remove-identity', payload, {
      headers: { Authorization: `Bearer ${accessId}` },
    });

    if (res?.status === 200) {
      showSuccessToast(`${identity.provider || identity.connection} account unlinked successfully.`);

      // optimistic local update
      setIdentities(prev =>
        Array.isArray(prev)
          ? prev.filter(i =>
              !(i.provider === payload.provider && i.userId === payload.userId && i.connection === payload.connection)
            )
          : []
      );

      // refresh user data
      const token = localStorage.getItem('access_token');
      if (token && typeof getUser === 'function') {
        try { await getUser(token); } catch (e) { console.warn('refresh failed', e); }
      }
    } else {
      showErrorToast('Failed to unlink account. Please try again.');
    }
  } catch (err) {
    console.error('Unlink failed', err);
    showErrorToast('Failed to unlink account. Please try again.');
  } finally {
    setUnlinking(false);
  }
};

const openMfaPopupAndPoll = (url) => {
  // 1) Open popup
  const w = window.open(
    url,
    'auth0_mfa_enroll',
    'width=460,height=720,menubar=no,toolbar=no,location=no,status=no'
  );
  if (!w) {
    showErrorToast('Popup blocked. Please allow popups and try again.');
    return;
  }

  // 2) Poll MFA status every 10s, but stop polling if popup is closed
  const startedAt = Date.now();
  const maxMs = 5 * 60 * 1000; // 5 minutes (adjust if you like)
  const interval = setInterval(async () => {
    try {
      // Stop polling if popup is closed
      if (w.closed) {
        clearInterval(interval);
        return;
      }
      const res = await dispatch(fetchMfaStatus());
      const payload = res?.payload;
       setMfaEnabled(payload);
      const expired = Date.now() - startedAt > maxMs;
      if (expired) {
        clearInterval(interval);
        try { if (!w.closed) w.close(); } catch (_) {}
      }
    } catch {
      // ignore single poll errors
    }
  }, 5000);
};

// Use the shared uploadToS3 utility from utils
// Example usage in your component:
// await uploadToS3(file, { user, setImageUploading, setProgresses, dispatch, setUserData, showSuccessToast, showErrorToast, setErrorMessages, getUser });

   useEffect(() => {
      const token = localStorage.getItem('access_token');
      // Only fetch if token exists, run once on mount
      if (token) {
        getUser(token);
      }
      // eslint-disable-next-line
    }, []);
    // On mount: Use IP geolocation API to set country and currency if not set
  useEffect(() => {
    // Only run if country or currency is NA
    if ((isEmptyProfileValue(userData.country) || isEmptyProfileValue(userData.defaultCurrency)) && typeof window !== "undefined") {
      fetch("https://ipapi.co/json/")
        .then((res) => res.json())
        .then((data) => {
          if (data && data.country) {
            // Find country object
            const countryObj = countries.find((c) => c.cca2 === data.country);
            let currency = userData.defaultCurrency;
            if (countryObj && countryObj.currencies && Object.keys(countryObj.currencies).length > 0) {
              const currencyCode = Object.keys(countryObj.currencies)[0];
              if (currencyOptions.some((opt) => opt.value === currencyCode)) {
                currency = currencyCode;
              }
            }
            setUserData((prev) => ({
              ...prev,
              country: isEmptyProfileValue(prev.country) ? data.country : prev.country,
              defaultCurrency: isEmptyProfileValue(prev.defaultCurrency) ? currency : prev.defaultCurrency,
            }));
          }
        })
        .catch(() => {});
    }
  }, []);

  // Redux for update user
  const dispatch = useDispatch();
  const updating = useSelector((state) => state.updateUser?.updating);
  const updateError = useSelector((state) => state.updateUser?.updateError);

  // Utility to safely parse date string to Date object or return null
  function parseDateSafe(date) {
    if (!date) return null;
    if (date instanceof Date && !isNaN(date)) return date;
    // Try to parse string in dd/MM/yyyy or ISO format
    if (typeof date === 'string') {
      // Try ISO first
      const iso = new Date(date);
      if (!isNaN(iso)) return iso;
      // Try dd/MM/yyyy 
      const parts = date.split('/');
      if (parts.length === 3) {
        const [dd, mm, yyyy] = parts;
        const parsed = new Date(`${yyyy}-${mm}-${dd}`);
        if (!isNaN(parsed)) return parsed;
      }
    }
    return null;
  }

    // Fetch MFA status on component mount
  useEffect(() => {
    (async () => {
      try {
        await dispatch(fetchMfaStatus());
      } catch (e) {
        // Optionally handle error
      }
    })();
  }, [dispatch]);
  // Format countries data for react-select
  const countryOptions = useMemo(() => {
    return countries
      .map((country) => ({
        value: country.cca2,
        label: country.name.common,
        flag: country.flag,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [])

  // State for active tab
  const [activeTab, setActiveTab] = useState(getInitialTab())
  const [plansUS, setPlansUS] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  // derive current plan id from user object if available
  const userCurrentPlanId = user?.subscription?.planId || user?.planId || user?.plan || user?.currentPlan || null;
  // Fetch legacy access data when tab is clicked
  useEffect(() => {
    if (activeTab === "legacyAccessSetting") {
      const accessToken = localStorage.getItem('access_token');
      api.get('/sharing/legacy', {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
        .then((res) => {
          // Optionally handle GET response here
          if (res.data && res.data.contactInfo) {
            setCollaborators([res.data.contactInfo]);
          } else {
            setCollaborators([]);
          }
          setLegacyAccessData(res.data);
        })
        .catch((err) => {
          // Optionally handle GET error here
          console.error('GET /sharing/legacy error:', err);
        });
    }
  }, [activeTab]);

  // Fetch plans for region US
  const fetchPlansRegionUS = async () => {
    setPlansLoading(true);
    try {
      const res = await api.get('/plans/region/USD', { headers: getAuthHeaders() });
      // keep same format as other calls - assume res.data.results or res.data
      const data = res.data?.results || res.data || [];
      setPlansUS(data.plans);
    } catch (e) {
      console.error('Failed to fetch plans for US region', e);
      setPlansUS([]);
    } finally {
      setPlansLoading(false);
    }
  }

  // State for edit mode
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [isEditingDetails, setIsEditingDetails] = useState(false)
  const [showSocialPopup, setShowSocialPopup] = useState(false)

  const [isOtpSent, setIsOtpSent] = useState(false)
  const [isEmailVerified, setIsEmailVerified] = useState(false)
  const [otpTimer, setOtpTimer] = useState(0)
  const [imageUploading, setImageUploading] = useState(false);
 // If updatePhone exists, update profile and move value to phone
// Replace your existing "If updatePhone exists, update profile and move value to phone" useEffect


  // User data state, initialize from API user if available
// Helper to split phone into phoneCode and phoneNumber
// Helper to map array socialLinks to object for UI
function mapSocialLinksArrayToObject(socialLinksArr) {
  if (!Array.isArray(socialLinksArr)) return {
    facebook: "",
    instagram: "",
    youtube: "",
    twitter: "",
  };
  const obj = {
    facebook: "",
    instagram: "",
    youtube: "",
    twitter: "",
  };
  socialLinksArr.forEach(link => {
    if (link.platform && obj.hasOwnProperty(link.platform)) {
      obj[link.platform] = link.url || "";
    }
  });
  // fallback defaults if all blank
  Object.keys(obj).forEach(key => {
    if (!obj[key]) {
      obj[key] = `www.${key}.com/`;
    }
  });
  return obj;
}
function splitPhone(phone) {
  if (!phone) return { phoneCode: '', phoneNumber: '' };
  const digits = phone.replace(/\D/g, '');
  if (digits.length <= 10) return { phoneCode: '', phoneNumber: digits };
  const phoneNumber = digits.slice(-10);
  const phoneCode = digits.slice(0, digits.length - 10);
  return { phoneCode, phoneNumber };
}
const initialPhone = splitPhone(user?.phone);
const [userData, setUserData] = useState({
  fullName: user?.name || "",
  dateOfBirth: user?.dateOfBirth ? String(user.dateOfBirth) : "",
  // Prefer `defaultCurrency` but fall back to backend `currency` field
  defaultCurrency: sanitizeProfileField(user?.defaultCurrency || user?.currency),
  gender: ["male", "female", "other"].includes((user?.gender || '').toLowerCase()) ? user.gender : "",
  email: user?.email || "",
  address: sanitizeProfileField(user?.address?.street),
  phoneNumber: sanitizeProfileField(initialPhone.phoneNumber),
  phoneCode: initialPhone.phoneCode || "",
  country: sanitizeProfileField(user?.address?.country),
  bio: sanitizeProfileField(user?.bio),
  profileImage: user?.profileImage || profilePhoto,
  coverImage: user?.coverImage || coverPhoto,
  socialLinks: mapSocialLinksArrayToObject(user?.socialLinks),
});

  // Update userData if user info from API changes
  useEffect(() => {
    if (user) {
      const { phoneCode, phoneNumber } = splitPhone(user.phone);
      setUserData((prev) => ({
        ...prev,
        fullName: user.name || prev.fullName,
        dateOfBirth: user.dateOfBirth ? formatDateToDDMMYYYY(user.dateOfBirth) : "",
  // Prefer backend `defaultCurrency`, then `currency`, then previous
  defaultCurrency: sanitizeProfileField(user.defaultCurrency || user.currency || prev.defaultCurrency),
        gender: ['male', 'female', 'other'].includes((user.gender || '').toLowerCase())
          ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1).toLowerCase()
          : '',
        email: user.email || prev.email,
        address: sanitizeProfileField(user.address?.street),
        phoneCode: phoneCode || prev.phoneCode,
        phoneNumber: sanitizeProfileField(phoneNumber === "null" ? "" : phoneNumber),
        country: sanitizeProfileField(user.address?.country),
        bio: sanitizeProfileField(user.bio),
        profileImage: user.profileImage || prev.profileImage,
        coverImage: user.coverImage || prev.coverImage,
        socialLinks: mapSocialLinksArrayToObject(user.socialLinks),
        
      }));

      // Helper to format date to dd/MM/yyyy
      function formatDateToDDMMYYYY(date) {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d)) return date;
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      }
    }
  }, [user]);

  // Auto-update backend when defaultCurrency is determined/changed client-side
  // useEffect(() => {
  //   // Run only when we have a logged in user and a real currency value
  //   if (!user || !user.id) return;
  //   const curr = userData?.defaultCurrency;
  //   if (!curr || curr === 'NA') return;

  //   // Prevent repeating auto-update in same session: store last auto-updated currency
  //   const lastAutoCurrencyKey = `last_auto_currency_${user.id}`;
  //   const lastAuto = localStorage.getItem(lastAutoCurrencyKey);

  //   // If backend already has the same currency, skip
  //   if ((user.defaultCurrency || '') === curr) {
  //     // still record it to avoid future redundant calls
  //     try { localStorage.setItem(lastAutoCurrencyKey, curr); } catch (e) {}
  //     return;
  //   }

  //   // If we already auto-updated to this currency, skip
  //   if (lastAuto === curr) return;

  //   // If a Redux update is already in progress, skip auto-update to avoid race/dupe
  //   if (updating) return;

  //   // Prevent re-entrancy in same render cycle
  //   if (autoCurrencyUpdateRef.current) return;
  //   autoCurrencyUpdateRef.current = true;

  //   // Build payload only containing currency to minimize side effects
  //   const payload = { currency: curr };
  //   (async () => {
  //     try {
  //       await dispatch(updateUser({ id: user.id || user._id, data: payload }));
  //       // record that we auto-updated for this user
  //       try { localStorage.setItem(lastAutoCurrencyKey, curr); } catch (e) {}
  //       showSuccessToast('Default currency saved automatically');
  //     } catch (err) {
  //       console.warn('Auto currency update failed', err);
  //     } finally {
  //       // release lock after small delay to avoid immediate re-run
  //       setTimeout(() => { autoCurrencyUpdateRef.current = false; }, 500);
  //     }
  //   })();

  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [userData?.defaultCurrency, user, dispatch]);


// place this AFTER your `const [userData, setUserData] = useState(...)`
const pendingPhoneRef = useRef(null);
// 'idle' | 'in-progress' | 'done'
const phoneUpdateStateRef = useRef('idle');
// in-memory lock to prevent duplicate auto-updates
const autoCurrencyUpdateRef = useRef(false);

useEffect(() => {
  // Read update_phone once on mount and stash it
  const updatePhone = localStorage.getItem('update_phone');
  if (updatePhone && updatePhone.length > 0) {
    pendingPhoneRef.current = updatePhone;

    // Update UI optimistically so user sees it
    setUserData(prev => ({ ...prev, phoneNumber: updatePhone }));
  }
}, []);

// Only trigger dispatch when user becomes available AND we have a pending phone,
// and ensure we only do it once with phoneUpdateStateRef as a lock.
const updatePhoneIfNeeded = async () => {
  const updatePhone = localStorage.getItem('update_phone');
  if (!updatePhone) return;

  if (phoneUpdateStateRef.current !== 'idle') {
    return;
  }

  const userId = user?.id || user?._id;
  if (!userId) {
    console.warn('[profile] no userId yet, skipping phone update');
    return;
  }

  phoneUpdateStateRef.current = 'in-progress';

  // sanitize: remove spaces, keep '+' if present
  const trimmed = updatePhone.trim();
  const keepPlus = trimmed.startsWith('+');
  const digitsOnly = trimmed.replace(/[^\d]/g, '');
  const sanitized = digitsOnly ? (keepPlus ? `+${digitsOnly}` : digitsOnly) : '';

  if (!sanitized) {
    console.warn('[profile] sanitized phone empty, aborting');
    phoneUpdateStateRef.current = 'idle';
    return;
  }

  // Only send phone
  const payload = { phone: sanitized };
  try {
    const result = await dispatch(updateUser({ id: userId, data: payload }));
    phoneUpdateStateRef.current = 'done';
    localStorage.setItem('phone', sanitized);
    localStorage.removeItem('update_phone');
    pendingPhoneRef.current = null;
    showSuccessToast('Phone updated successfully');
    return result;
  } catch (err) {
    console.error('[profile] phone update failed', err);
    phoneUpdateStateRef.current = 'idle';
    showErrorToast('Failed to update phone automatically. Please update from profile.');
    throw err;
  }
};

  // Notification settings state
  const [notifications, setNotifications] = useState({
    itemUpdate: true,
    itemComment: false,
    buyerReview: true,
    ratingReminders: false,
    meetupsNearYou: false,
    companyNews: true,
  })

  // Handle notification toggle
  const handleNotificationChange = (key) => {
    setNotifications({
      ...notifications,
      [key]: !notifications[key],
    })
  }

  // Handle basic details form submission

const handleBasicDetailsSubmit = async (values) => {
  setDetailsUpdating(true);
  if (!user?.id) {
    console.error("User ID is missing, cannot update user!");
    setDetailsUpdating(false);
    return;
  }

  const updatedUserData = {
    ...userData,
    ...values,
  };

  setUserData(updatedUserData);

  const payload = mapUserDataToApiPayload(updatedUserData);

  try {
    await dispatch(updateUser({ id: user.id, data: payload }));
    showSuccessToast('Profile details updated successfully!');
    const token = localStorage.getItem('access_token');
    if (token && typeof getUser === 'function') {
      // refresh canonical user so other areas reflect the change
      try { await getUser(token); } catch (e) { /* non-fatal */ }
    }
  } catch (err) {
    showErrorToast('Failed to update profile details!');
  } finally {
    setIsEditingDetails(false);
    setDetailsUpdating(false);
  }
};


  // Handle social links form submission
  const handleSocialLinksSubmit = (values) => {
    setUserData({
      ...userData,
      socialLinks: values,
    })
    setShowSocialPopup(false)

  }
  // Handle profile image change (with upload)

const handleFileChange = async (event) => {
  const file = event.target?.files?.[0];
  if (!file) return;

  try {
    await uploadToS3(file, {
      user,
      setImageUploading,
      setProgresses,
      dispatch,
      setUserData,
      showSuccessToast,
      showErrorToast,
      setErrorMessages,
      getUser,
    });
    setIsEditingProfile(false);

  } catch (e) {
    // error already handled in uploadToS3
  }
};



  // Save profile changes

  const saveProfileChanges = async () => {
    // const payload = {
    //   name: userData.fullName,
    //   dateOfBirth: userData.dateOfBirth,
    //   currency: userData.defaultCurrency,
    //   region: userData.defaultCurrency,
    //   gender: userData.gender ? userData.gender.toLowerCase() : '',
    //   email: userData.email,
    //   address: userData.address,
    //   dialCode: userData.phoneCode,
    //   phone: userData.phoneNumber,
    //   country: userData.country,
    //   bio: userData.bio,
    //   profileImage: userData.profileImage,
    //   coverImage: userData.coverImage,
    //   socialLinks: userData.socialLinks,
    // };
    // try {
    //   const res = await dispatch(updateUser({ id: user?._id, data: payload }));
    //   // if the thunk returned updated user data, update the store immediately
    //   if (res && res.payload) {
    //     try { dispatch(setUser(res.payload)); 
    //        setIsEditingProfile(false); 
    //     } catch (e) {}
    //   } else {
    //     const token = localStorage.getItem('access_token');
    //     if (token && typeof getUser === 'function') {
    //       try { await getUser(token); } catch (e) { /* ignore */ }
    //       setIsEditingProfile(false);
    //     }
    //   }
    // } catch (err) {
    //   console.warn('saveProfileChanges failed', err);
    // } finally {

      
    // }
          setIsEditingProfile(false);
  };
  // Save details changes using Redux updateUserSlice

  const saveDetailsChanges = async () => {
    const payload = {
      name: userData.fullName,
      dateOfBirth: userData.dateOfBirth,
      currency: userData.defaultCurrency,
      region: userData.defaultCurrency,
      gender: userData.gender ? userData.gender.toLowerCase() : '',
      email: userData.email,
      address: userData.address,
      dialCode: userData.phoneCode,
      phone: userData.phoneNumber,
      country: userData.country,
      bio: userData.bio,
      profileImage: userData.profileImage,
      coverImage: userData.coverImage,
      socialLinks: userData.socialLinks,
    };
    try {
      const res = await dispatch(updateUser({ id: user?._id, data: payload }));
      if (res && res.payload) {
        try { dispatch(setUser(res.payload)); } catch (e) {}
      } else {
        const token = localStorage.getItem('access_token');
        if (token && typeof getUser === 'function') {
          try { await getUser(token); } catch (e) { /* ignore */ }
        }
      }
    } catch (err) {
      console.warn('saveDetailsChanges failed', err);
    } finally {
      setIsEditingDetails(false);
    }
  };
  // Loader overlay for save
  // Place this in your JSX: {updating && <Loader message="Saving changes..." />} 
  // Optionally show error: {updateError && <div className="error-message">{updateError}</div>}

   // Handle phone code change
  // Update both userData and Formik field value if setFieldValue is provided
  const handlePhoneCodeChange = (selectedOption, setFieldValue) => {
    setUserData({
      ...userData,
      phoneCode: selectedOption.value,
    });
    if (setFieldValue) {
      setFieldValue('phoneCode', selectedOption.value);
    }
  }

  // Handle select change
  // Update both userData and Formik field value if setFieldValue is provided
  const handleSelectChange = (selectedOption, field, setFieldValue) => {
    setUserData({
      ...userData,
      [field]: selectedOption.value,
    });
    if (setFieldValue) {
      setFieldValue(field, selectedOption.value);
    }
  }

// Handle send OTP
  const handleSendOtp = () => {
    setIsOtpSent(true)
    setOtpTimer(60) // 60 seconds timer
    // Here you would typically call your API to send OTP


    // Start countdown timer
    const timer = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  // Handle OTP verification
  const handleOtpVerification = (values) => {
    const otpCode = `${values.otp1}${values.otp2}${values.otp3}${values.otp4}`
    // Here you would typically verify OTP with your backend


    // Simulate successful verification
    setIsEmailVerified(true)
    setIsOtpSent(false)
  }

  // Handle OTP input focus and auto-advance
  const handleOtpInputChange = (e, nextFieldName, setFieldValue) => {
    const value = e.target.value
    if (value.length === 1 && /^\d$/.test(value)) {
      setFieldValue(e.target.name, value)
      // Auto-focus next input
      if (nextFieldName) {
        const nextInput = document.querySelector(`input[name="${nextFieldName}"]`)
        if (nextInput) nextInput.focus()
      }
    } else if (value.length === 0) {
      setFieldValue(e.target.name, "")
    }
  }

  // Get country name from code
  const getCountryName = (code) => {
    if (isEmptyProfileValue(code)) return 'NA';
    const country = countryOptions.find((country) => country.value === code)
    return country ? country.label : displayProfileValue(code)
  }

  // Get currency name from code
  const getCurrencyName = (code) => {
    if (isEmptyProfileValue(code)) return 'NA';
    const currency = currencyOptions.find((currency) => currency.value === code)
    return currency ? currency.label : displayProfileValue(code)
  }

  // Calculate profile completion percentage
const requiredFields = [
  userData.fullName,
  !isEmptyProfileValue(userData.dateOfBirth),
  // userData.defaultCurrency && !isEmptyProfileValue(userData.defaultCurrency),
  userData.gender,
  userData.email,
  // userData.country && !isEmptyProfileValue(userData.country),
  !isEmptyProfileValue(userData.phoneNumber),
];
const filledCount = requiredFields.filter(Boolean).length;
const completionPercent = Math.round((filledCount / requiredFields.length) * 100);
// Now you can use completionPercent wherever you want to show the percentage
useEffect(() => {
  if (completionPercent !== undefined && !isNaN(completionPercent)) {
    localStorage.setItem('profileCompletionPercent', completionPercent);
  }
}, [completionPercent]);

// Open popup as soon as mfaTicketUrl is set
useEffect(() => {
  if (mfaTicketUrl) {
    setShowMfaPopup(true);
  }
}, [mfaTicketUrl]);


// Utility to check if a provider is already linked
function isProviderLinked(identities, provider) {
  if (!Array.isArray(identities)) return false;
  return identities.some(identity => identity.provider === provider);
}

const [identities, setIdentities] = useState([]);

// Fetch identities from user_info in localStorage on mount
  useEffect(() => {
    if (user && Array.isArray(user.identities)) {
      setIdentities(user.identities);
    }
  }, [user]);

const isFacebookLinked = isProviderLinked(identities, 'facebook');
const isGoogleLinked = isProviderLinked(identities, 'google-oauth2');
const defaultCollaboratorCountryCode = useMemo(
  () => getDialCodeFromUserProfile(user),
  [user]
);

const collaboratorValidationSchema = Yup.object({
  firstName: Yup.string().min(2, "First name must be at least 2 characters").required("First name is required"),
  lastName: Yup.string().min(2, "Last name must be at least 2 characters").required("Last name is required"),
  countryCode: Yup.string().required("Country code is required"),
  mobile: Yup.string()
  .matches(/^\d{7,15}$/, "Mobile number must be between 7 and 15 digits")
  .required("Mobile number is required"),
  email: Yup.string().email("Please enter a valid email address").required("Email is required"),
})

// Custom validation for inactivePeriod
function validateInactivePeriod(inactivePeriod) {
  const errors = {};
  if (Number(inactivePeriod.days) === 0) {
    errors.inactiveDays = "Inactivity Days must be greater than 0";
  }
  if (Number(inactivePeriod.month) === 0) {
    errors.gracePeriodDays = "Grace Period Days must be greater than 0";
  }
  return errors;
}

  const [inactivePeriod, setInactivePeriod] = useState({
    days: 0,
    month: 0,
    year: 0,
  })

  const [collaboratorForm, setCollaboratorForm] = useState({
    firstName: "",
    lastName: "",
    countryCode: "",
    mobile: "",
    email: "",
  })

  const [collaborators, setCollaborators] = useState([])

const handleAddCollaborator = async (values, { resetForm }) => {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      showErrorToast('Not authenticated. Please login again.');
      return;
    }

    // Check if a legacy collaborator already exists
    if (collaborators.length > 0) {
      showErrorToast('Only one legacy collaborator is allowed. Please remove the existing one to add a new collaborator.');
      return;
    }

    const payload = {
      email: values.email,
      name: `${values.firstName || ''} ${values.lastName || ''}`.trim(),
      phone: `${values.countryCode || ''}${values.mobile || ''}`.replace(/\s+/g, ''),
      inactivityDays: Number(inactivePeriod.days) || 0,
      gracePeriodDays: Number(inactivePeriod.month) || 0,
      notes: values.notes || '',
    };

    const res = await api.post('/sharing/legacy', payload, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const created = res?.data || { ...values };

    // Update the collaborators state with the newly created collaborator
    setCollaborators([created]);

    showSuccessToast('Collaborator added successfully!');
    resetForm();
    setInactivePeriod({ days: 0, month: 0 });

    // Refresh the legacy access data to ensure the UI is updated
    try {
      const listRes = await api.get('/sharing/legacy', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (listRes.data && listRes.data.contactInfo) {
        setCollaborators([listRes.data.contactInfo]);
      }
    } catch (e) {
      console.warn('refresh failed', e);
    }
  } catch (err) {
    console.error('Add collaborator error:', err);
    const serverMessage =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      'Failed to add collaborator.';
    showErrorToast(`Failed to add collaborator: ${serverMessage}`);
  }
};



  const handleRemoveCollaborator = (index) => {
    // Call DELETE API to /sharing/legacy with Authorization token
    const accessToken = localStorage.getItem('access_token');
    api.delete('/sharing/legacy', {
      headers: { Authorization: `Bearer ${accessToken}` },
      // Optionally, you can send collaborator info in data/body if needed
      // data: { ...collaborators[index] }
    })
      .then((res) => {
        showSuccessToast('Collaborator removed successfully!');
        setCollaborators([]);
      })
      .catch((err) => {
        showErrorToast('Failed to remove collaborator.');
      });
  }

  return (
    
    <Layout>
      {/* Loader overlay for user loading or updating */}
      {(loading || updating || socialUpdating || detailsUpdating || imageUploading) && <Loader />}
      <div className="profile-container">
            {/* Tabs */}
            <div className="profile-tabs">
                <ul className="nav">
                <li className="nav-item">
                    <button
                    className={`nav-link ${activeTab === "profile" ? "active" : ""}`}
                    onClick={() => setActiveTab("profile")}
                    >
                    Profile
                    </button>
                </li>

                {/* <li className="nav-item">
                    <button
                    className={`nav-link ${activeTab === "twoStep" ? "active" : ""}`}
                    onClick={() => setActiveTab("twoStep")}
                    >
                    Two Step Verification
                    </button>
                </li> */}
                <li className="nav-item">
                    <button
                    className={`nav-link ${activeTab === "billing" ? "active" : ""}`}
                    onClick={() => { setActiveTab("billing"); fetchPlansRegionUS(); }}
                    >
                    Manage Plan/Billing
                    </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "legacyAccessSetting" ? "active" : ""}`}
                    onClick={() => setActiveTab("legacyAccessSetting")}
                  >
                    Legacy Access Setting
                  </button>
                </li>
              </ul>
            </div>

            {/* Profile Content */}
            {activeTab === "profile" && (
                <div className="profile-content">
                <div className="row g-4">
                    {/* Profile Card */}
                    <div className="col-xl-4 col-lg-12 col-sm-12">
                    <div className="card profile-card">
                        <div className="cover-image-container">
                        <img src={userData.coverImage || "/placeholder.svg"} className="cover-image" alt="Cover" />
                        {/* {isEditingProfile && (
                            <div className="cover-image-edit">
                            <label htmlFor="cover-upload" className="btn btn-sm btn-light rounded-circle">
                                <i className="bi bi-camera"></i>
                            </label>
                            <input
                                id="cover-upload"
                                type="file"
                                accept="image/*"
                                className="d-none"
                                onChange={handleCoverImageChange}
                            />
                            </div>
                        )} */}
                        <button
                            className={`btn btn-sm ${isEditingProfile ? "btn-success" : "btn-light"} edit-btn`}
                            onClick={isEditingProfile ? saveProfileChanges : () => setIsEditingProfile(true)}
                        >
                            <i className={`bi ${isEditingProfile ? "bi-x-lg" : "bi-pencil"}`}></i>
                            {isEditingProfile ? "Cancel" : "Edit"}
                        </button>
                        </div>
                        <div className="profile-image-container">
                        <span className="progress-ring" style={{ '--progress': completionPercent ? completionPercent : 0 }}>
                          <img src={user?.image ? user?.image : userpic} className="profile-image" alt="Profile" />
                        </span>
                        {isEditingProfile && (
                            <div className="profile-image-edit">
                            <label htmlFor="profile-upload" className="btn btn-sm btn-light rounded-circle">
                                <i className="bi bi-camera"></i>
                            </label>
                            <input
                                id="profile-upload"
                                type="file"
                                accept="image/*"
                                className="d-none"
                                onChange={handleFileChange}
                            />
                            </div>
                        )}
                        </div>
                        <div className="card-body text-center">
                        <h5 className="card-title">{userData.fullName}</h5>
                        <div className="social-icons">
                            <a href={`${userData.socialLinks.facebook ? userData.socialLinks.facebook : '#'}`} className="social-icon facebook" target="_blank">
                            <i className="bi bi-facebook"></i>
                            </a>
                            <a href={`${userData.socialLinks.instagram ? userData.socialLinks.instagram : '#'}`} className="social-icon instagram" target="_blank">
                            <i className="bi bi-instagram"></i>
                            </a>
                            <a href={`${userData.socialLinks.youtube ? userData.socialLinks.youtube : '#'}`} className="social-icon youtube" target="_blank">
                            <i className="bi bi-youtube"></i>
                            </a>
                            <a href={`${userData.socialLinks.twitter}`} className="social-icon twitter" target="_blank">
                            <i className="bi bi-twitter-x"></i>
                            </a>
                            <button className="social-icon edit-social" onClick={() => setShowSocialPopup(true)}>
                            <i className="bi bi-pencil"></i>
                            </button>
                        </div>
                        </div>
                    </div>
                    </div>

                    {/* Two Factor and Billing Cards */}
                        {/* Two Factor Authentication Card */}
                        <div className="col-xl-4 col-lg-6 col-sm-12">
                        <div className="card h-100 info-card">
                            <div className="card-body text-center d-flex flex-column justify-content-between">
                            <div>     
                            {mfaStatus && mfaStatus.mfaEnabled ? (
                              <div className="icon-container two-factor">
                                <i className="bi bi-shield-check"></i>
                              </div>
                            ) : (
                              <div className="icon-container two-error">
                                <i className="bi bi-shield-check"></i>
                              </div>
                            )}
                            <h5 className="card-title">
                              {mfaStatus && mfaStatus.mfaEnabled ? '2FA Enabled!' : '2FA Disabled!'}
                            </h5>
                            <p className="card-text text-muted">
                              {mfaStatus && mfaStatus.mfaEnabled
                                ? 'Your account is now 100% secure from unauthorized login.'
                                : 'Two factor authentication is currently disabled for your account.'}
                            </p>
                            </div>
                            {mfaStatus && mfaStatus.mfaEnabled ? (
                              <div>
                              <button
                                className="btn btn-secondary gray-btn"
                                style={{ fontSize: '14px' }}
                                onClick={async () => {
                                  try {
                                    const result = await dispatch(disableMfa());
                                    showSuccessToast('MFA disabled successfully!');
                                  } catch (e) {
                                    showErrorToast('Failed to disable MFA.');
                                  }
                                }}
                                disabled={enableLoading}
                              >
                                Disable MFA
                              </button>
                              </div>
                            ) : (
                              <div>
                              <button
                                className="btn btn-primary manage-btn"
                                onClick={async () => {
                                  const result = await dispatch(enableMfa());
                                  const payload = result?.payload;
                                  const ticketUrl = payload?.enrollment?.ticket_url;
                                  if (ticketUrl) {
                                    openMfaPopupAndPoll(ticketUrl);
                                  } else {
                                    showErrorToast('Could not start 2FA enrollment.');
                                  }
                                }}
                                disabled={enableLoading || !!enableLoading || (mfaStatus && mfaStatus.mfaEnabled)}
                              >
                                {enableLoading ? 'Enabling...' : (mfaStatus && mfaStatus.mfaEnabled ? 'Enabled' : 'Enable MFA ')}
                              </button>
                              </div>
                            )}
                           



                            {enableError && (
                              <div className="text-danger small mt-2">{typeof enableError === 'string' ? enableError : 'Failed to enable authentication.'}</div>
                            )}
                            {/* {enableSuccess && !enableLoading && !mfaTicketUrl && (
                              <div className="text-success small mt-2">Authentication enabled successfully!</div>
                            )} */}
                            </div>
                            
                        </div>
                        </div>

                        {/* Billing Card */}
                        {/* <div className="col-xl-4 col-lg-6 col-sm-12 mb-4">
                        <div className="card h-100 info-card">
                            <div className="card-body text-center d-flex flex-column justify-content-between">
                            <div>
                              <div className="icon-container billing">
                                  <i className="bi bi-credit-card"></i>
                              </div>
                              <h5 className="card-title">Billings & Plan</h5>
                              <p className="card-text text-muted">Manage your billing and payment details.</p>
                            </div>
                            <div>
                              <button className="btn btn-primary manage-btn d-inline-flex" onClick={fetchPlansRegionUS}>Manage Now</button>
                            </div>
                            </div>
                        </div>
                        </div> */}

                        {/* Verify Phone/Email */}
                        {(() => {
                          const identities = Array.isArray(user?.identities) ? user.identities : [];
                          const smsIdentities = identities.filter(i => i.provider === 'sms');
                          const auth0Count = identities.filter(i => i.provider === 'auth0').length;
                          const hasMultipleAuth0 = auth0Count > 0;
                          // Always show EmailPhoneLink card
                          return (
                            <div className="col-xl-4 col-lg-6 col-sm-12">
                              <div className="card h-100 info-card">
                                <EmailPhoneLink hasSms={smsIdentities} hasMultipleAuth0={hasMultipleAuth0} auth0Data={userData} onRequestPhoneUpdate={updatePhoneIfNeeded}/>
                              </div>
                            </div>
                          );
                        })()}
                    </div>

                <div className="row g-4">
                    {/* Basic Details Card */}
                    <div className="col-xl-8 col-lg-12">
                    <div className="card details-card">
                      <div className="card-header">
                        <h5 className="mb-0">Basic Details</h5>
                        <div className="header-actions">
                          <button
                            className="btn btn-sm btn-outline-changes-color"
                            onClick={() => {
                              if (!user?.email) {
                                showErrorToast('Email not found. Please try again.');
                                return;
                              }
                              webAuth.changePassword({
                                connection: 'Username-Password-Authentication',
                                email: user.email,
                              }, (err) => {
                                if (err) {
                                  showErrorToast('Failed to send reset email: ' + (err.description || err.message));
                                } else {
                                  showSuccessToast('Password reset email sent! Check your inbox.');
                                  setTimeout(() => {
                                    localStorage.clear();
                                    sessionStorage.clear();
                                    window.location.href = '/login';
                                  }, 2000);
                                }
                              });
                            }}
                          >
                            <i className="bi bi-key"></i>
                            Change Password
                          </button>
                          <button
                            className="btn btn-sm btn-light edit-btn header-edit-btn"
                            onClick={() => setIsEditingDetails(!isEditingDetails)}
                          >
                            <i className={`bi ${isEditingDetails ? "bi-x-lg" : "bi-pencil"}`}></i>
                            {isEditingDetails ? "Cancel" : "Edit"}
                          </button>
                        </div>
                      </div>
                      <div className="card-body">
                        {isEditingDetails ? (
                          <Formik
                            initialValues={{
                              fullName: userData.fullName,
                              dateOfBirth: (() => {
                                const dob = userData.dateOfBirth;
                                if (!dob) return '';
                                if (dob instanceof Date) return dob;
                                // If ISO string (2025-06-05T00:00:00.000Z)
                                if (typeof dob === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(dob)) {
                                  const d = new Date(dob);
                                  if (!isNaN(d)) return d;
                                }
                                // If dd/MM/yyyy
                                if (typeof dob === 'string' && /^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
                                  const [day, month, year] = dob.split('/');
                                  const d = new Date(`${year}-${month}-${day}`);
                                  if (!isNaN(d)) return d;
                                }
                                // fallback
                                return parseDateSafe(dob);
                              })(),
                              defaultCurrency: userData.defaultCurrency,
                              gender: isEmptyProfileValue(userData.gender) ? '' : userData.gender,
                              email: userData.email,
                              address: sanitizeProfileField(userData.address),
                              phoneCode: userData.phoneCode,
                              phoneNumber: sanitizeProfileField(userData.phoneNumber),
                              country: sanitizeProfileField(userData.country),
                              bio: sanitizeProfileField(userData.bio),
                            }}
                            validationSchema={Yup.object().shape({
                              fullName: Yup.string()
                                .min(3, "Name must be at least 3 characters")
                                .max(50, "Name must be less than 50 characters")
                                .required('Full Name is required')
                                .matches(
                                  /^[a-zA-Z\s\-'.]{3,50}$/,
                                  "Only letters, spaces, hyphens, apostrophes, and periods are allowed."
                                ),
                              dateOfBirth: Yup.date()
                                .typeError('Date of Birth is required')
                                .required('Date of Birth is required'),
                                // .max(new Date(), 'Date of Birth cannot be in the future'),
                              defaultCurrency: Yup.string().required('Currency is required'),
                              gender: Yup.string().oneOf(['Female', 'Male', 'Other'], 'Gender is required').required('Gender is required'),
                              email: Yup.string().email('Invalid email').required('Email is required'),
                              address: Yup.string()
                                .min(3, "Address must be at least 3 characters")
                                .max(150, "Address must be less than 150 characters")
                                .required('Address is required')
                                .matches(
                                  /^[a-zA-Z0-9\s,.\-#/'()]{3,150}$/,
                                  "Only letters, numbers, spaces, and common symbols (, . - / # ' ( )) are allowed."
                                )
                                .matches(
                                  /^\S(?:.*\S)?$/,
                                  "Space at the beginning or end is not allowed"
                                ),
                              // phoneCode: Yup.string().required('Country code is required'),
                              // phoneNumber: Yup.string()
                              //   .required('Phone number is required')
                              //   .matches(/^[0-9]{7,15}$/, 'Phone number must be 7-15 digits'),
                              country: Yup.string().required('Country is required'),
                              bio: Yup.string()
                                .min(3, "Bio must be at least 3 characters")
                                .max(300, "Bio must be less than 300 characters")
                                .required('Bio is required')
                                .matches(
                                  /^[a-zA-Z0-9\s,.\-#/'()]{3,300}$/,
                                  "Only letters, numbers, spaces, and common symbols (, . - / # ' ( )) are allowed."
                                ),
                            })}
                            validateOnChange={true}
                            onSubmit={(values) => {
                              // Convert dateOfBirth to string in mm/dd/yyyy before submit
                              let dob = values.dateOfBirth;
                              if (dob instanceof Date && !isNaN(dob)) {
                                const month = String(dob.getMonth() + 1).padStart(2, '0');
                                const day = String(dob.getDate()).padStart(2, '0');
                                const year = dob.getFullYear();
                                dob = `${month}/${day}/${year}`;
                              }
                              const submitValues = { ...values, dateOfBirth: dob };
                              handleBasicDetailsSubmit(submitValues);
                            }}
                          >
                            {({ values, setFieldValue, errors, touched }) => (
                              <Form>
                                <div className="row mb-4">
                                  <div className="col-xl-4 col-lg-6">
                                    <label className="form-label text-muted">Full Name</label>
                                    <Field
                                      type="text"
                                      name="fullName"
                                      maxLength="50"
                                      className={`form-control ${errors.fullName && touched.fullName ? "is-invalid" : ""}`}
                                    >
                                      {({ field, form }) => (
                                        <input
                                          {...field}
                                          type="text"
                                          maxLength="50"
                                          className={`form-control ${errors.fullName && touched.fullName ? "is-invalid" : ""}`}
                                          onKeyDown={(e) => {
                                            // Check if adding this character would exceed the limit
                                            if (e.target.value.length >= 50 && e.key !== 'Backspace' && e.key !== 'Delete' && !e.ctrlKey && !e.metaKey) {
                                              form.setFieldError('fullName', 'Name must be less than 50 characters');
                                              form.setFieldTouched('fullName', true, false);
                                              e.preventDefault();
                                            }
                                          }}
                                          onChange={(e) => {
                                            field.onChange(e);
                                            // Clear error if user deletes characters
                                            if (e.target.value.length < 50 && form.errors.fullName) {
                                              form.setFieldError('fullName', undefined);
                                            }
                                          }}
                                        />
                                      )}
                                    </Field>
                                    <ErrorMessage name="fullName" component="div" className="invalid-feedback" />
                                  </div>
                                  <div className="col-xl-4 col-lg-6">
                                    <label className="form-label text-muted">Date Of Birth</label>
                                    <div className="form-control ">
                                    {/* old code for dob picker */}
                                    {/* <Field name="dateOfBirth">
                                        {({ field, form }) => {
                                          const today = new Date();

                                          return (
                                            <DatePicker
                                              {...field}
                                              selected={parseDateSafe(field.value)}
                                              openToDate={getSafeCalendarDate(parseDateSafe(field.value))}
                                              onChange={(date) => {
                                                const safeDate = date > today ? today : date;
                                                form.setFieldValue("dateOfBirth", safeDate);
                                                form.setFieldTouched("dateOfBirth", true, false);
                                              }}
                                              onYearChange={(year) => {
                                                if (year > today.getFullYear()) {
                                                  // Force calendar back to current month/year
                                                  form.setFieldValue("dateOfBirth", today);
                                                }
                                              }}
                                              onBlur={() =>
                                                form.setFieldTouched("dateOfBirth", true, false)
                                              }
                                              dateFormat="dd/MM/yyyy"
                                              className={`${
                                                form.errors.dateOfBirth && form.touched.dateOfBirth
                                                  ? "is-invalid"
                                                  : ""
                                              }`}
                                              placeholderText="DD/MM/YYYY"
                                              showMonthDropdown
                                              showYearDropdown
                                              dropdownMode="select"
                                              minDate={new Date(1900, 0, 1)}
                                              maxDate={today}
                                            />
                                          );
                                        }}
                                      </Field> */}

                                      {/* //custom header for disabling future months/years */}
                                      <Field name="dateOfBirth">
                                        {({ field, form }) => {
                                          const today = new Date();
                                          const currentYear = today.getFullYear();
                                          const currentMonth = today.getMonth();

                                          const years = Array.from(
                                            { length: currentYear - 1900 + 1 },
                                            (_, i) => currentYear - i
                                          );

                                          const months = [
                                            "January", "February", "March", "April", "May", "June",
                                            "July", "August", "September", "October", "November", "December"
                                          ];

                                          return (
                                            <DatePicker
                                              {...field}
                                              selected={parseDateSafe(field.value)}
                                              openToDate={getSafeCalendarDate(parseDateSafe(field.value))}
                                              maxDate={today}
                                              dateFormat="dd/MM/yyyy"
                                              placeholderText="DD/MM/YYYY"
                                              className={`${
                                                form.errors.dateOfBirth && form.touched.dateOfBirth
                                                  ? "is-invalid"
                                                  : ""
                                              }`}
                                              onChange={(date) => {
                                                const safeDate = date > today ? today : date;
                                                form.setFieldValue("dateOfBirth", safeDate);
                                                form.setFieldTouched("dateOfBirth", true, false);
                                              }}
                                              onYearChange={(year) => {
                                                if (year > currentYear) {
                                                  form.setFieldValue("dateOfBirth", today);
                                                }
                                              }}
                                              onBlur={() =>
                                                form.setFieldTouched("dateOfBirth", true, false)
                                              }
                                              renderCustomHeader={({
                                                date,
                                                changeYear,
                                                changeMonth,
                                              }) => {
                                                const selectedYear = date.getFullYear();

                                                return (
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      justifyContent: "center",
                                                      gap: "8px",
                                                      marginBottom: "8px",
                                                    }}
                                                  >
                                                    {/* Year dropdown */}
                                                    <select
                                                      value={selectedYear}
                                                      onChange={(e) =>
                                                        changeYear(Number(e.target.value))
                                                      }
                                                    >
                                                      {years.map((year) => (
                                                        <option
                                                          key={year}
                                                          value={year}
                                                          disabled={year > currentYear}
                                                        >
                                                          {year}
                                                        </option>
                                                      ))}
                                                    </select>

                                                    {/* Month dropdown */}
                                                    <select
                                                      value={date.getMonth()}
                                                      onChange={(e) =>
                                                        changeMonth(Number(e.target.value))
                                                      }
                                                    >
                                                      {months.map((month, index) => {
                                                        const disableMonth =
                                                          selectedYear > currentYear ||
                                                          (selectedYear === currentYear &&
                                                            index > currentMonth);

                                                        return (
                                                          <option
                                                            key={month}
                                                            value={index}
                                                            disabled={disableMonth}
                                                          >
                                                            {month}
                                                          </option>
                                                        );
                                                      })}
                                                    </select>
                                                  </div>
                                                );
                                              }}
                                            />
                                          );
                                        }}
                                      </Field>
                                    </div>
                                    {errors.dateOfBirth && touched.dateOfBirth && (
                                      <div className="invalid-feedback d-block">{errors.dateOfBirth}</div>
                                    )}
                                  </div>
                                  <div className="col-xl-4 col-lg-6">
                                    <label className="form-label text-muted">Default Currency</label>
                                    <Select
                                      className={`react-select-container${errors.defaultCurrency && touched.defaultCurrency ? ' is-invalid-select' : ''}`}
                                      classNamePrefix="react-select"
                                      options={currencyOptions}
                                      value={currencyOptions.find((option) => option.value === values.defaultCurrency)}
                                      onChange={(option) => setFieldValue("defaultCurrency", option.value)}
                                      formatOptionLabel={(option) => (
                                        <div className="currency-option">
                                          <span>{option.label}</span>
                                        </div>
                                      )}
                                    />
                                    {errors.defaultCurrency && touched.defaultCurrency && (
                                      <div className="text-danger small mt-1">{errors.defaultCurrency}</div>
                                    )}
                                  </div>
                                </div>

                                <div className="row mb-4">
                                  <div className="col-xl-4 col-lg-6">
                                    <label className="form-label text-muted">Gender</label>
                                    <Select
                                      className={`react-select-container${errors.gender && touched.gender ? ' is-invalid-select' : ''}`}
                                      classNamePrefix="react-select"
                                      options={[
                                        { value: "Female", label: "Female" },
                                        { value: "Male", label: "Male" },
                                        { value: "Other", label: "Other" },
                                      ]}
                                      value={values.gender ? { value: values.gender, label: values.gender } : null}
                                      onChange={(option) => setFieldValue("gender", option ? option.value : "")}
                                      placeholder="Select Gender"
                                      isClearable
                                    />
                                    {errors.gender && touched.gender && (
                                      <div className="text-danger small mt-1">{errors.gender}</div>
                                    )}
                                  </div>
                                  <div className="col-xl-4 col-lg-6">
                                    <label className="form-label text-muted">Email</label>
                                    <Field
                                      type="email"
                                      name="email"
                                      className={`form-control ${errors.email && touched.email ? "is-invalid" : ""}`}
                                      // readOnly ={!identities.some(item => item?.connection === "sms")}
                                    />
                                    <ErrorMessage name="email" component="div" className="invalid-feedback" />
                                  </div>
                                  <div className="col-xl-4 col-lg-6">
                                    <label className="form-label text-muted">Address</label>
                                    <Field
                                      type="text"
                                      name="address"
                                      maxLength="150"
                                      className={`form-control ${errors.address && touched.address ? "is-invalid" : ""}`}
                                    >
                                      {({ field, form }) => (
                                        <input
                                          {...field}
                                          type="text"
                                          maxLength="150"
                                          className={`form-control ${errors.address && touched.address ? "is-invalid" : ""}`}
                                          onKeyDown={(e) => {
                                            // Check if adding this character would exceed the limit
                                            if (e.target.value.length >= 150 && e.key !== 'Backspace' && e.key !== 'Delete' && !e.ctrlKey && !e.metaKey) {
                                              form.setFieldError('address', 'Address must be less than 150 characters');
                                              form.setFieldTouched('address', true, false);
                                              e.preventDefault();
                                            }
                                          }}
                                          onChange={(e) => {
                                            field.onChange(e);
                                            // Clear error if user deletes characters
                                            if (e.target.value.length < 150 && form.errors.address) {
                                              form.setFieldError('address', undefined);
                                            }
                                          }}
                                        />
                                      )}
                                    </Field>
                                    <ErrorMessage name="address" component="div" className="invalid-feedback" />
                                  </div>
                                </div>

                                <div className="row mb-4">
                                  {/* <div className="col-xl-4 col-lg-6">
                                    <label className="form-label text-muted">Mobile</label>
                                    <div className="input-group">
                                      <div className="phone-code-select" style={{ minWidth: '90px', marginRight: '8px' }}>
                                        
                                       <CountrySelectReact
                                          value={code}
                                          onChange={(newVal) => {
                                            // newVal expected e.g. "+91" or "91" depending on component
                                            setCode(newVal);

                                            // normalize: remove '+' and spaces
                                            const normalized = (newVal || '').toString().replace(/\+/g, '').replace(/\s+/g, '');

                                            // update local userData
                                            setUserData(prev => ({ ...prev, phoneCode: normalized }));

                                            // if you're inside Formik render and have access to setFieldValue:
                                            if (typeof setFieldValue === 'function') {
                                              setFieldValue('phoneCode', normalized);
                                            }
                                          }}
                                          maxWidth={130}
                                          isDisabled={Array.isArray(identities) && identities.some(item => item?.connection === "sms")}
                                        />
                                      </div>
                                      <Field name="phoneNumber">
                                        {({ field }) => (
                                          <input
                                            {...field}
                                            type="text"
                                            className={`form-control phone-number-input ${errors.phoneNumber && touched.phoneNumber ? "is-invalid" : ""}`}
                                            placeholder="Phone number"
                                            maxLength={10}
                                            pattern="[0-9 ]*"
                                            readOnly={Array.isArray(identities) && identities.some(item => item?.connection === "sms")}
                                          />
                                        )}
                                      </Field>
                                    </div>
                                    {((errors.phoneCode && touched.phoneCode) ||
                                      (errors.phoneNumber && touched.phoneNumber)) && (
                                      <div className="text-danger small mt-1">
                                        {errors.phoneCode || errors.phoneNumber}
                                      </div>
                                    )}
                                  </div> */}
                                  <div className="col-xl-4 col-lg-6">
                                    <label className="form-label text-muted">Country</label>
                                    <Select
                                      className={`react-select-container${errors.country && touched.country ? ' is-invalid-select' : ''}`}
                                      classNamePrefix="react-select"
                                      options={countryOptions}
                                      value={countryOptions.find((option) => option.value === values.country)}
                                      onChange={(option) => setFieldValue("country", option.value)}
                                      formatOptionLabel={(option) => (
                                        <div className="country-option">
                                          <span>
                                            {option.flag} {option.label}
                                          </span>
                                        </div>
                                      )}
                                    />
                                    {errors.country && touched.country && (
                                      <div className="text-danger small mt-1">{errors.country}</div>
                                    )}
                                  </div>
                                  <div className="col-xl-4 col-lg-6">
                                    <label className="form-label text-muted">Bio</label>
                                    <Field
                                      type="text"
                                      name="bio"
                                      maxLength="300"
                                      className={`form-control ${errors.bio && touched.bio ? "is-invalid" : ""}`}
                                    >
                                      {({ field, form }) => (
                                        <input
                                          {...field}
                                          type="text"
                                          maxLength="300"
                                          className={`form-control ${errors.bio && touched.bio ? "is-invalid" : ""}`}
                                          onKeyDown={(e) => {
                                            // Check if adding this character would exceed the limit
                                            if (e.target.value.length >= 300 && e.key !== 'Backspace' && e.key !== 'Delete' && !e.ctrlKey && !e.metaKey) {
                                              form.setFieldError('bio', 'Bio must be less than 300 characters');
                                              form.setFieldTouched('bio', true, false);
                                              e.preventDefault();
                                            }
                                          }}
                                          onChange={(e) => {
                                            field.onChange(e);
                                            // Clear error if user deletes characters
                                            if (e.target.value.length < 300 && form.errors.bio) {
                                              form.setFieldError('bio', undefined);
                                            }
                                          }}
                                        />
                                      )}
                                    </Field>
                                    <ErrorMessage name="bio" component="div" className="invalid-feedback" />
                                  </div>
                                </div>

                                <div className="row">
                                  <div className="col-12">
                                    <button type="submit" className="btn btn-outline-primary me-2">
                                      <i className="bi bi-pencil"></i> Save Changes
                                    </button>
                                    {/* <button
                                      type="button"
                                      className="btn btn-secondary"
                                      onClick={() => setIsEditingDetails(false)}
                                    >
                                      <i className="bi bi-x-lg"></i> Cancel
                                    </button> */}
                                  </div>
                                </div>
                              </Form>
                            )}
                          </Formik>
                        ) : (
                          <>
                            <div className="row mb-4">
                              <div className={`col-xl-4 col-lg-6 card-shadow ${!isEmptyProfileValue(userData?.fullName) ? '' : 'bd-callout-warning'}`}>
                                <label className="form-label text-muted">Full Name</label>
                                <p className="detail-value">{displayProfileValue(userData.fullName)}</p>
                              </div>
                               <div className={`col-xl-4 col-lg-6 card-shadow ${!isEmptyProfileValue(userData?.dateOfBirth) ? '' : 'bd-callout-warning'}`}>
                                <label className="form-label text-muted">Date Of Birth</label>
                                <p className="detail-value">{formatDateOfBirthDisplay(userData.dateOfBirth)}</p>
                              </div>
                              <div className={`col-xl-4 col-lg-6 card-shadow ${!isEmptyProfileValue(userData?.defaultCurrency) ? '' : 'bd-callout-warning'}`}>
                                <label className="form-label text-muted">Default Currency</label>
                                <p className="detail-value">{getCurrencyName(userData.defaultCurrency)}</p>
                              </div>
                            </div>

                            <div className="row mb-4">
                              <div className={`col-xl-4 col-lg-6 card-shadow ${!isEmptyProfileValue(userData?.gender) ? '' : 'bd-callout-warning'}`}>
                                <label className="form-label text-muted">Gender</label>
                                <p className="detail-value">{displayProfileValue(userData.gender)}</p>
                              </div>
                              <div className={`col-xl-4 col-lg-6 card-shadow ${!isEmptyProfileValue(userData?.email) ? '' : 'bd-callout-warning'}`}>
                                <label className="form-label text-muted">Email</label>
                                <p className="detail-value">{displayProfileValue(userData.email)}</p>
                              </div>
                              <div className={`col-xl-4 col-lg-6 card-shadow ${!isEmptyProfileValue(userData?.address) ? '' : 'bd-callout-warning'}`}>
                                <label className="form-label text-muted">Address</label>
                                <p className="detail-value">{displayProfileValue(userData.address)}</p>
                              </div>
                            </div>

                            <div className="row">
                             {/* <div className={`col-xl-4 col-lg-6 card-shadow ${!isEmptyProfileValue(userData?.phoneNumber) ? '' : 'bd-callout-warning'}`}>
                                <label className="form-label text-muted">Mobile</label>
                                <p className="detail-value">
                                {userData.phoneCode ? `+${userData.phoneCode} ${userData.phoneNumber.replace(/\s+/g, '')}` : userData.phoneNumber.replace(/\s+/g, '')}
                                </p>
                              </div> */}
                              <div className={`col-xl-4 col-lg-6 card-shadow ${!isEmptyProfileValue(userData?.country) ? '' : 'bd-callout-warning'}`}>
                                <label className="form-label text-muted">Country</label>
                                <p className="detail-value">{getCountryName(userData.country)}</p>
                              </div>
                              <div className={`col-xl-4 col-lg-6 card-shadow ${!isEmptyProfileValue(userData?.bio) ? '' : 'bd-callout-warning'}`}>
                                <label className="form-label text-muted">Bio</label>
                                <p className="detail-value">{displayProfileValue(userData.bio)}</p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    </div>

                    {/* Facebook Connect */}
                      <div className="col-xl-4 col-lg-6 col-sm-12">
                        <div className="card h-100 info-card">

                          <div className="card-body text-center d-flex flex-column justify-content-between">

                            <div>
                              <div className="d-flex mb-2 gap-2">
                                <Button
                                  variant={socialActiveTab === 'facebook' ? 'solid-tab' : 'outline-tab'}
                                  className="flex-1 text-sm justify-content-center d-flex align-items-center btn-outline-changes-color"
                                  onClick={() => setSocialActiveTab('facebook')}
                                >
                                  <i className="bi bi-facebook"></i>
                                  <span className="leading-snug">Facebook</span>
                                </Button>
                                <Button
                                  variant={socialActiveTab === 'google' ? 'solid-tab' : 'outline-tab'}
                                  className="flex-1 text-sm justify-content-center d-flex align-items-center btn-outline-changes-color"
                                  onClick={() => setSocialActiveTab('google')}
                                >
                                  <i className="bi bi-google"></i>
                                  <span className="leading-snug">Google</span>
                                </Button>
                              </div>
                            </div>

                            {socialActiveTab === 'facebook' && (
                            <>
                            {(() => {
                              const fbIdentity = findIdentity('facebook');
                              const isLinked = !!fbIdentity;

                              const primaryProvider =
                              typeof user?.auth0UserId === 'string'
                              ? user.auth0UserId.split('|')[0]
                              : null;

                              const cannotUnlinkPrimary = primaryProvider === 'facebook';

                              return (
                                <>
                                <div>
                                  <div className="icon-container facebook-connect">
                                    <i className="bi bi-facebook"></i>
                                  </div>
                                  <p className="card-text text-muted">
                                    {isLinked
                                      ? "Your Facebook account is connected to your profile."
                                      : "Connect your facebook account to your profile."}
                                  </p>
                                </div>
                                <div>
                                  <button
                                    className="btn btn-primary manage-btn"
                                    onClick={() => {
                                      const accessToken = localStorage.getItem('access_token');
                                      if (accessToken) localStorage.setItem('access_id', accessToken);
                                      localStorage.setItem('linkaccount', 'true');
                                      localStorage.setItem('linkaccount_time', Date.now().toString());
                                      loginWithSocial('facebook');
                                    }}
                                    disabled={isLinked}
                                    type="button"
                                  >
                                    {isLinked ? "Connected" : "Connect Now"}
                                  </button>


                                {isLinked && primaryProvider !== 'facebook' && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger ms-2"
                                      style={{ fontSize: '14px', marginTop: '10px' }}
                                      onClick={(e) => handleUnlinkIdentity(fbIdentity, e)}
                                      disabled={unlinking || cannotUnlinkPrimary}
                                      title={cannotUnlinkPrimary ? 'Cannot unlink primary login method' : 'Unlink Facebook'}
                                    >
                                      {unlinking ? 'Unlinking...' : 'Unlink'}
                                    </button>
                                  )}
                                </div>
                                </>
                              );
                            })()}
                            </>
                          )}

                          {socialActiveTab === 'google' && (
                            <>
                            {(() => {
                              const googleIdentity = findIdentity('google-oauth2');
                              const isLinked = !!googleIdentity;
                              const primaryProvider = user?.auth0UserId?.split?.('|')?.[0] || null;
                              return (
                                <>
                                <div>
                                  <div className="icon-container google-connect">
                                    <i className="bi bi-google"></i>
                                  </div>
                                  <p className="card-text text-muted">
                                    {isLinked
                                      ? "Your Google account is connected to your profile."
                                      : "Connect your google account to your profile."}
                                  </p>
                                </div>
                                <div>
                                  <button
                                    className="btn btn-primary manage-btn"
                                    onClick={() => {
                                      const accessToken = localStorage.getItem('access_token');
                                      if (accessToken) localStorage.setItem('access_id', accessToken);
                                      localStorage.setItem('linkaccount', 'true');
                                      localStorage.setItem('linkaccount_time', Date.now().toString());
                                      loginWithSocial('google-oauth2');
                                    }}
                                    disabled={isLinked}
                                  >
                                    {isLinked ? "Connected" : "Connect Now"}
                                  </button>

                                  {isLinked && primaryProvider !== 'google-oauth2' && (
                                    <button
                                      type="button"
                                      className="btn btn-outline-danger ms-2" style={{ fontSize: '14px',marginTop: '10px' }}
                                      onClick={(e) => handleUnlinkIdentity(googleIdentity, e)}
                                      disabled={unlinking}
                                    >
                                      {unlinking ? 'Unlinking...' : 'Unlink'}
                                    </button>
                                  )}
                                </div>
                                </>
                              );
                            })()}
                            </>
                          )}
                          </div>
                        </div>
                      </div>

                    {/* Notifications Card */}
                    {/* <div className="col-xl-4 col-lg-12 mb-4">
                    <div className="card notifications-card">
                        <div className="card-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">Notifications</h5>
                        </div>
                        <div className="card-body">
                        <div className="notification-item">
                            <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="itemUpdate"
                                checked={notifications.itemUpdate}
                                onChange={() => handleNotificationChange("itemUpdate")}
                            />
                            <label className="form-check-label" htmlFor="itemUpdate">
                                Item update notifications
                            </label>
                            </div>
                        </div>

                        <div className="notification-item">
                            <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="itemComment"
                                checked={notifications.itemComment}
                                onChange={() => handleNotificationChange("itemComment")}
                            />
                            <label className="form-check-label" htmlFor="itemComment">
                                Item comment notifications
                            </label>
                            </div>
                        </div>

                        <div className="notification-item">
                            <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="buyerReview"
                                checked={notifications.buyerReview}
                                onChange={() => handleNotificationChange("buyerReview")}
                            />
                            <label className="form-check-label" htmlFor="buyerReview">
                                Buyer review notifications
                            </label>
                            </div>
                        </div>

                        <div className="notification-item">
                            <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="ratingReminders"
                                checked={notifications.ratingReminders}
                                onChange={() => handleNotificationChange("ratingReminders")}
                            />
                            <label className="form-check-label" htmlFor="ratingReminders">
                                Rating reminders notifications
                            </label>
                            </div>
                        </div>

                        <div className="notification-item">
                            <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="meetupsNearYou"
                                checked={notifications.meetupsNearYou}
                                onChange={() => handleNotificationChange("meetupsNearYou")}
                            />
                            <label className="form-check-label" htmlFor="meetupsNearYou">
                                Meetups near you notifications
                            </label>
                            </div>
                        </div>

                        <div className="notification-item">
                            <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="companyNews"
                                checked={notifications.companyNews}
                                onChange={() => handleNotificationChange("companyNews")}
                            />
                            <label className="form-check-label" htmlFor="companyNews">
                                Company news notifications
                            </label>
                            </div>
                        </div>
                        </div>
                    </div>
                    </div> */}

                </div>
                </div>
            )}



            {/* Two Step Verification Content */}
            {activeTab === "twoStep" && (
                <div className="two-step-content">
                <div className="card">
                    <div className="card-body">
                    <h5>Two Step Verification Settings</h5>
                    <p>This tab would contain two-step verification settings.</p>
                    </div>
                </div>
                </div>
            )}

            {/* Billing Content */}
      {activeTab === "billing" && (
    <SubscriptionManagement plans={plansUS} loading={plansLoading} refreshPlans={fetchPlansRegionUS} currentPlan={userCurrentPlanId} />
      )}

            {/* Legacy Access Setting Content */}
            {activeTab === "legacyAccessSetting" && (
              <div className="legacy-content">
                <div className="row">
                  <div className="col-lg-7">
                    {/* In-Active Period Setting */}
                    <div className="card mb-4">
                      <h5 className="mb-4">In-Active Period Setting</h5>
                      {(() => {
                        const inactivePeriodLabelStyle = { fontSize: '14px', fontWeight: 500 };
                        const inactivePeriodSelectStyles = {
                          control: (base) => ({ ...base, fontSize: '14px', fontWeight: 400, border: 0, borderBottom: '1px solid #E2E2E5', borderRadius: 0, boxShadow: 'none' }),
                          option: (base) => ({ ...base, fontSize: '14px', fontWeight: 400 }),
                        };
                        const dayOptions = Array.from({ length: 32 }, (_, i) => ({ value: i, label: String(i) }));
                        return (
                          <div className="row g-3">
                            <div className="col-4">
                              <label className="in-active-period" style={inactivePeriodLabelStyle}>Inactivity Days</label>
                              <Select
                                classNamePrefix="react-select"
                                options={dayOptions}
                                value={dayOptions.find((o) => o.value === Number(inactivePeriod.month))}
                                onChange={(opt) => setInactivePeriod({ ...inactivePeriod, month: opt.value })}
                                styles={inactivePeriodSelectStyles}
                                isSearchable={false}
                              />
                            </div>
                            <div className="col-4">
                              <label className="in-active-period" style={inactivePeriodLabelStyle}>Grace Period Days</label>
                              <Select
                                classNamePrefix="react-select"
                                options={dayOptions}
                                value={dayOptions.find((o) => o.value === Number(inactivePeriod.days))}
                                onChange={(opt) => setInactivePeriod({ ...inactivePeriod, days: opt.value })}
                                styles={inactivePeriodSelectStyles}
                                isSearchable={false}
                              />
                            </div>
                            {/* Info Box */}
                            <div className="col-12">
                              <div className="info-box">
                                <div className="info-icon">i</div>
                                <p className="info-text">
                                  Only one legacy collaborator is allowed. Please remove the existing one to add a new collaborator.
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Add Collaborator */}
                    <div className="card mb-4">
                      <h5 className="mb-4">Add Collaborator</h5>
                      <Formik
                        initialValues={{
                          firstName: "",
                          lastName: "",
                          mobile: "",
                          countryCode: defaultCollaboratorCountryCode,
                          email: "",
                          notes: "",
                        }}
                        enableReinitialize
                        validationSchema={collaboratorValidationSchema}
                        validate={() => validateInactivePeriod(inactivePeriod)}
                        onSubmit={handleAddCollaborator}
                      >
                        {({ errors, touched }) => (
                          <Form>
                            {(errors.inactiveDays || errors.gracePeriodDays) && (
                              <div className="alert alert-danger mb-2">
                                {errors.inactiveDays && <div>{errors.inactiveDays}</div>}
                                {errors.gracePeriodDays && <div>{errors.gracePeriodDays}</div>}
                              </div>
                            )}
                            <div className="row g-3">
                              <div className="col-6">
                                <label className="in-active-period">First Name</label>
                                <Field
                                  type="text"
                                  name="firstName"
                                  className={`form-control ${errors.firstName && touched.firstName ? "is-invalid" : ""}`}
                                  placeholder="Enter First Name"
                                />
                                <ErrorMessage name="firstName" component="div" className="invalid-feedback" />
                              </div>
                              <div className="col-6">
                                <label className="in-active-period">Last Name</label>
                                <Field
                                  type="text"
                                  name="lastName"
                                  className={`form-control ${errors.lastName && touched.lastName ? "is-invalid" : ""}`}
                                  placeholder="Enter Last Name"
                                />
                                <ErrorMessage name="lastName" component="div" className="invalid-feedback" />
                              </div>
                            </div>

                            <div className="row g-3 mt-2">
                              <div className="col-6">
                                <label className="in-active-period">Mobile</label>
                                <div className="input-group">
  <Field name="countryCode">
    {({ field, form }) => (
      <CountrySelectReact
        value={field.value}
        onChange={val => form.setFieldValue("countryCode", val)}
        maxWidth={120}
      />
    )}
  </Field>
  <Field name="mobile">
    {({ field, form }) => (
      <input
        {...field}
        type="tel"
        className={`form-control ${errors.mobile && touched.mobile ? "is-invalid" : ""}`}
        placeholder="Enter Mobile Number"
        style={{ minWidth: 120 }}
        maxLength={15}
        minLength={7}
        pattern="[0-9]*"
        inputMode="numeric"
        onChange={(e) => {
          const value = e.target.value.replace(/\D/g, '');
          form.setFieldValue("mobile", value);
        }}
      />
    )}
  </Field>
   <ErrorMessage name="countryCode" component="div" className="invalid-feedback" />
  <ErrorMessage name="mobile" component="div" className="invalid-feedback" />
</div>

                              </div>
                              <div className="col-6">
                                <label className="in-active-period">Email</label>
                                <Field
                                  type="email"

                                  name="email"
                                  className={`form-control ${errors.email && touched.email ? "is-invalid" : ""}`}
                                  placeholder="Enter Email ID"
                                />
                                <ErrorMessage name="email" component="div" className="invalid-feedback" />
                              </div>
                            </div>

                            <div className="row g-3 mt-2">
                              <div className="col-12">
                                <label className="in-active-period">Notes</label>
                                <Field
                                  as="textarea"
                                  name="notes"
                                  className={`form-control ${errors.notes && touched.notes ? "is-invalid" : ""}`}
                                  placeholder="Enter any notes"
                                />
                                <ErrorMessage name="notes" component="div" className="invalid-feedback" />
                              </div>
                              </div>
                            <button type="submit" className="btn btn-primary mt-4 add-btn">
                              Add
                            </button>
                          </Form>
                        )}
                      </Formik>
                    </div>
                  </div>

                  {/* Right Side - Legacy Access */}
                  <div className="col-lg-5">
                    <div className="card mb-4">
                      <h5 className="mb-4">Legacy Access</h5>

                      {collaborators.length === 0 ? (
                        <div className="no-collaborator">
                          <div className="no-collaborator-icon">!</div>
                          <h6>No Collaborator Added!</h6>
                          <p>Add first collaborator to provide legacy access</p>
                        </div>
                      ) : (
                        
                          <div className="collaborator-card">
                            <div className="me-3" style={{ width: "50px", height: "50px" }}>
                              <img src={useepic} alt="Avatar" className="avatar-img" width={50} />
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex align-items-center justify-content-between">
                                <div>
                                  <h6 className="collaborator-name">
                                    {collaborators[0]?.name || collaborators.name}
                                  </h6>
    
                                </div>
                                <div className="dropdown">
                                  <button className="btn btn-sm dropdown-btn" type="button" data-bs-toggle="dropdown">
                                    ⋮
                                  </button>
                                  <ul className="dropdown-menu">
                                    <li>
                                      <button className="dropdown-item d-flex align-items-center" onClick={() => handleLegacyEditClick()}>
                                        <span className="me-2">✏</span> Edit
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="dropdown-item d-flex align-items-center text-danger"
                                        onClick={() => handleRemoveCollaborator()}
                                      >
                                        <span className="me-2">✕</span> Remove
                                      </button>
                                    </li>
                                  </ul>
                                </div>
                              </div>
                              <p className="card-text mb-0 mt-1">{collaborators[0]?.email || collaborators.email}</p>
                            </div>
                          </div>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
            )}

        {/* Legacy Access Edit Modal */}
{showLegacyEditModal && (
  <div className="modal-overlay" style={{position: 'fixed', top:0, left:0, right:0, bottom:0, background: 'rgba(0,0,0,0.3)', zIndex: 9999}}>
    <div className="modal-content" style={{background: '#fff', padding: 24, borderRadius: 8, maxWidth: 500, margin: '80px auto', boxShadow: '0 2px 16px rgba(0,0,0,0.15)'}}>
      <h5 class="">Edit Collaborator (Legacy Access)</h5>
      <p class="edit-modal-subtitl mb-4" >Update collaborator details and legacy access settings</p>
      {(() => {
        const modalLabelStyle = { fontSize: '14px', fontWeight: 500 };
        const modalSelectStyles = {
          control: (base) => ({ ...base, fontSize: '14px', fontWeight: 400, marginTop: 4, border: 0, borderBottom: '1px solid #E2E2E5', borderRadius: 0, boxShadow: 'none' }),
          option: (base) => ({ ...base, fontSize: '14px', fontWeight: 400 }),
        };
        const modalDayOptions = [
          { value: '', label: 'Select' },
          ...Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
        ];
        return (
          <>
            <div className="edit-form-group mb-4">
              <label className="in-active-period" style={modalLabelStyle}>Inactivity Days</label>
              <Select
                classNamePrefix="react-select"
                options={modalDayOptions}
                value={modalDayOptions.find((o) => o.value === Number(legacyEditData.inactivityDays)) || modalDayOptions[0]}
                onChange={(opt) => setLegacyEditData({ ...legacyEditData, inactivityDays: opt.value })}
                styles={modalSelectStyles}
                isSearchable={false}
              />
            </div>
            <div className="edit-form-group mb-4">
              <label className="in-active-period" style={modalLabelStyle}>Grace Period Days</label>
              <Select
                classNamePrefix="react-select"
                options={modalDayOptions}
                value={modalDayOptions.find((o) => o.value === Number(legacyEditData.gracePeriodDays)) || modalDayOptions[0]}
                onChange={(opt) => setLegacyEditData({ ...legacyEditData, gracePeriodDays: opt.value })}
                styles={modalSelectStyles}
                isSearchable={false}
              />
            </div>
          </>
        );
      })()} 
      <div className="edit-form-group mb-4">  
      <label>Notes: </label>
        <textarea
          value={legacyEditData.notes}
          onChange={e => setLegacyEditData({ ...legacyEditData, notes: e.target.value })}
          style={{width:'100%', marginTop:4}}
          className="form-control "
        />
     </div> 
      {legacyEditError && <div className="error-message" style={{color:'red', marginBottom:8}}>{legacyEditError}</div>}
      <div className="modal-actions" style={{display:'flex', gap:12, marginTop:16}}>
        <button onClick={handleLegacyEditSave} disabled={legacyEditLoading} style={{padding:'8px 16px'}} className="edit-btn-submit">
          {legacyEditLoading ? 'Saving...' : 'Save'}
        </button>
        <button onClick={() => setShowLegacyEditModal(false)} disabled={legacyEditLoading} style={{padding:'8px 16px'}} className="edit-btn-cancel">
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

        {/* Social Links Popup */}
        {showSocialPopup && (
          <div className="social-popup-overlay">
            <div className="social-popup">
              <div className="social-popup-header">
                <h5>Update Social Links</h5>
                <button className="btn-close" onClick={() => setShowSocialPopup(false)}></button>
              </div>
              <div className="social-popup-body">
                <Formik
  initialValues={{
    facebook: userData.socialLinks?.facebook || "",
    instagram: userData.socialLinks?.instagram || "",
    youtube: userData.socialLinks?.youtube || "",
    twitter: userData.socialLinks?.twitter || "",
  }}
  validationSchema={socialLinksSchema}
  onSubmit={(values) => {
    setSocialUpdating(true);
    const normalizeUrl = (url, platform) => {
      if (!url || url.trim() === "") {
        // Save default platform URL with https
        return `https://www.${platform}.com/`;
      }
      let trimmed = url.trim();
      if (!/^https?:\/\//i.test(trimmed)) {
        trimmed = `https://${trimmed}`;
      }
      return trimmed;
    };

    const platforms = ["facebook", "instagram", "youtube", "twitter"];

    const socialLinks = platforms
      .map((platform) => {
        const url = normalizeUrl(values[platform], platform);
        return url ? { platform, url } : null;
      })
      .filter(Boolean);

    const payload = {
      socialLinks, 
    };

    const result = dispatch(updateUser({ id: user?.id, data: payload }));
    (result && typeof result.then === 'function' ? result : Promise.resolve())
      .then(() => {
        showSuccessToast('Social links updated successfully!');
      })
      .catch(() => {
        showErrorToast('Failed to update social links!');
      })
      .finally(() => {
        setUserData((prev) => ({
          ...prev,
          socialLinks: mapSocialLinksArrayToObject(socialLinks),
        }));
        setShowSocialPopup(false);
        setSocialUpdating(false);
      });
  }}

>
  {({ errors, touched }) => (
    <Form>
      <div className="social-input-group">
        <div className="social-icon facebook">
          <i className="bi bi-facebook"></i>
        </div>
        <Field
          type="text"
          name="facebook"
          className={`form-control ${errors.facebook && touched.facebook ? "is-invalid" : ""}`}
          placeholder="Facebook URL"
        />
        <ErrorMessage name="facebook" component="div" className="invalid-feedback" />
      </div>

      <div className="social-input-group">
        <div className="social-icon instagram">
          <i className="bi bi-instagram"></i>
        </div>
        <Field
          type="text"
          name="instagram"
          className={`form-control ${errors.instagram && touched.instagram ? "is-invalid" : ""}`}
          placeholder="Instagram URL"
        />
        <ErrorMessage name="instagram" component="div" className="invalid-feedback" />
      </div>

      <div className="social-input-group">
        <div className="social-icon youtube">
          <i className="bi bi-youtube"></i>
        </div>
        <Field
          type="text"
          name="youtube"
          className={`form-control ${errors.youtube && touched.youtube ? "is-invalid" : ""}`}
          placeholder="YouTube URL"
        />
        <ErrorMessage name="youtube" component="div" className="invalid-feedback" />
      </div>

      <div className="social-input-group">
        <div className="social-icon twitter">
          <i className="bi bi-twitter-x"></i>
        </div>
        <Field
          type="text"
          name="twitter"
          className={`form-control ${errors.twitter && touched.twitter ? "is-invalid" : ""}`}
          placeholder="Twitter URL"
        />
        <ErrorMessage name="twitter" component="div" className="invalid-feedback" />
      </div>

      <div className="mt-3">
        <button type="submit" className="btn btn-success me-2">
          <i className="bi bi-check-lg"></i> Save Links
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => setShowSocialPopup(false)}>
          <i className="bi bi-x-lg"></i> Cancel
        </button>
      </div>
    </Form>
  )}
</Formik>

              </div>
            </div>
          </div>
        )}
        </div>
        
    </Layout>
    
  )
}

export default ProfileManagement
